import Controller from "csi/action/Controller";
import Key from "csi/core/Key";
import csi from "csi/main";
import CustomViewAttribute from "csi/middleware/customView/attributes/CustomViewAttribute";
import CustomViewAttributesStore, { CustomViewAttributesOptions } from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import localeProvider from "csi/page/localeProvider";
import csiPrefs from "csi/prefs";
import { routerStore } from "csi/state/rootStore";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import { DndStore } from "designSystem/dnd/index";
import declare from "dojo/_base/declare";

const ManageAttributesController = declare(Controller, {
	/** @property {CustomViewAttributesStore}  */
	attributesStore: null,

	/** @property {string} urlCustomView - custom view URL to manage (may not be created yet) */
	urlCustomView: null,

	/** @property {string} urlCustomViewContainer - custom view container URL (may not be created yet) */
	urlCustomViewContainer: null,

	/** @property {csi/view/Widget} viewWithAttributes - view containing attributes available to select */
	viewWithAttributes: null,

	/**
	 * Extension point to allow filtering of data points that are allowed in view
	 */
	canShowAttribute: function(key: Key) {
		return true;
	},

	createStore(): CustomViewAttributesStore {
		const { viewWithAttributes } = this;
		const specBuilder = viewWithAttributes.createSpecBuilder();
		const { attributeKeys } = viewWithAttributes;

		// determine options for store
		const attributesOptions: CustomViewAttributesOptions = {
			omitAttributeCallback: (key) => !this.canShowAttribute(key),
			noGroupSupport: true,
			noMatrixSupport: true,
			noVisibilitySupport: true,
			omitImages: true,
			omitReferencedImages: true,
		};

		// create store
		const attributesStore = new CustomViewAttributesStore(localeProvider, attributesOptions);

		// add levels
		attributesStore.addLevel({ specBuilder });

		// add selections
		attributesStore.addSelections(attributeKeys);

		// add dnd store
		const { canDrop } = attributesStore;
		const attributeDndStore = new DndStore<CustomViewAttribute>(canDrop);
		attributesStore.attributeDndStore = attributeDndStore;

		return attributesStore;
	},

	/**
	 * @override
	 * Resolve all data points in view
	 */
	onPreActionDeferreds(dfds: Promise<void>[]): void { 
		this.viewWithAttributes = this.viewWithAttributes || this.widget;
		this.urlCustomView = this.urlCustomView || this.m_urlNode;
	},

	/**
	 * @override
	 * Open dialog to select attributes
	 */
	preProcess(): void {
		const customView = this._getCustomView();
		const store = this.attributesStore = this.createStore();

		const popupProps = {
			onCancel: this.onCancel.bind(this),
			onSave: this.onSave.bind(this),
			store,
			titleRes: "Edit",
		};

		// if custom view already created, then container is known
		if (customView) {
			this.urlCustomViewContainer = customView.__Parent__;
		}

		routerStore.openInlineDialog("csi/middleware/customView/attributes/AttributesDialog", popupProps);
	},

	/**
	 * Save selected attributes to custom view
	 */
	toWriter(): void {
		const customView = this._getCustomView();
		const rc = this.viewWithAttributes.defView.getPageClass();
		const attributes = [];
		const keys = csi.hashArray(this.actionValue, (key, i) => {
			attributes.push(i);
			return i;
		});

		// create custom view
		const cn = this.m_writer.createNode(this.urlCustomView, csiPrefs.TYPE_VIEW, rc.getType(), null, true)
			.changeAttribute("Attributes", xml.ATTR_STRINGVECTOR, attributes)
			.changeAttribute("Keys", xml.ATTR_STRINGMAP, keys)
			.changeAttribute("TargetClass", xml.ATTR_STRING, rc.getType());

		// attach to container if custom view does not already exist
		if (!customView) {
			cn.attach(this.urlCustomViewContainer, csiPrefs.ATTR_PREFERENCES);
		}
	},

	/**
	 * Return node for custom view, if created
	 * @return {Node}
	 */
	_getCustomView(): C8Node {
		return this.m_cache.get(this.urlCustomView);
	},

	/**
	 * Do nothing when edit cancelled
	 */
	onCancel(): void {
		this.closeAttributesDialog();
		this.postProcess();
	},

	/**
	 * Convert selected attributes to extended keys and process
	 */
	onSave(): void {
		this.closeAttributesDialog();

		const view = this.viewWithAttributes;
		this.actionValue = this.attributesStore.attributeKeys;
		this.process();
	},

	/** close popup */
	closeAttributesDialog(): void {
		routerStore.closeInlineDialog();
	},		
});

export = ManageAttributesController;
